import React, { useEffect, useRef, useState, useCallback } from 'react';
import Hls from 'hls.js';

const Player = ({ channel }) => {
  const videoRef = useRef(null);
  const hlsRef = useRef(null);
  const containerRef = useRef(null);
  const [isMuted, setIsMuted] = useState(true); // Start muted by default
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showControls, setShowControls] = useState(true);
  const [controlsTimeout, setControlsTimeout] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [volume, setVolume] = useState(1);
  
  // Quality related states
  const [qualities, setQualities] = useState([]);
  const [currentQuality, setCurrentQuality] = useState('Auto');
  const [showQualityMenu, setShowQualityMenu] = useState(false);
  
  // Track if channel has been loaded for auto-play
  const [isChannelReady, setIsChannelReady] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const previousChannelRef = useRef(null);
  const playAttemptRef = useRef(false);
  const autoPlayDoneRef = useRef(false);

  // Handle resize
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Clean up HLS and reset states when channel changes
  useEffect(() => {
    // Reset states when channel changes
    setIsChannelReady(false);
    setIsLoading(true);
    playAttemptRef.current = false;
    autoPlayDoneRef.current = false;
    
    const video = videoRef.current;
    if (!video || !channel) {
      setIsChannelReady(false);
      setIsLoading(false);
      return;
    }

    // Check if this is a new channel
    const isNewChannel = previousChannelRef.current?.id !== channel.id;
    previousChannelRef.current = channel;

    // Clean up existing HLS instance
    if (hlsRef.current) {
      hlsRef.current.destroy();
      hlsRef.current = null;
    }

    const existingIframe = document.getElementById('embed-iframe');
    if (existingIframe) existingIframe.remove();

    if (!channel.url) {
      video.style.display = 'none';
      setIsChannelReady(false);
      setIsLoading(false);
      return;
    }

    video.style.display = 'block';
    // ALWAYS start muted for auto-play to work
    video.muted = true;
    setIsMuted(true);

    // Reset video src to ensure clean state
    video.removeAttribute('src');
    video.load();

    if (channel.type === 'hls' && Hls.isSupported()) {
      const hls = new Hls({ 
        enableWorker: true, 
        lowLatencyMode: true,
        maxBufferLength: 30,
        maxMaxBufferLength: 60,
        startFragPrefetch: true,
        testBandwidth: true
      });
      
      hls.loadSource(channel.url);
      hls.attachMedia(video);
      
      hls.on(Hls.Events.MANIFEST_PARSED, (event, data) => {
        // Extract quality levels from the manifest
        const levels = hls.levels.map((level, index) => {
          let height = level.height || 0;
          
          if (height === 0 && level.attrs) {
            const resolution = level.attrs.RESOLUTION;
            if (resolution) {
              const match = resolution.match(/(\d+)x(\d+)/);
              if (match) {
                height = parseInt(match[2], 10);
              }
            }
          }
          
          return {
            index: index,
            height: height,
            width: level.width || 0,
            bitrate: level.bitrate || 0,
            label: getQualityLabel(height, level.bitrate)
          };
        });
        
        const uniqueLevels = [];
        const seenLabels = new Set();
        for (const level of levels) {
          if (!seenLabels.has(level.label)) {
            seenLabels.add(level.label);
            uniqueLevels.push(level);
          }
        }
        
        setQualities(uniqueLevels);
        setCurrentQuality('Auto');
        
        // Auto-play on manifest parsed
        setIsChannelReady(true);
        setIsLoading(false);
        
        // Attempt to play immediately (video is muted, so it should work)
        if (!autoPlayDoneRef.current) {
          autoPlayDoneRef.current = true;
          const playPromise = video.play();
          if (playPromise !== undefined) {
            playPromise
              .then(() => {
                setIsPlaying(true);
                console.log('Auto-play successful (muted)');
              })
              .catch((err) => {
                console.log('Auto-play failed:', err);
                setIsLoading(false);
                // Try again with a small delay
                setTimeout(() => {
                  video.play().catch(() => {});
                }, 500);
              });
          }
        }
      });

      // Listen for quality level changes
      hls.on(Hls.Events.LEVEL_SWITCHED, (event, data) => {
        const level = hls.levels[data.level];
        if (level) {
          let height = level.height || 0;
          if (height === 0 && level.attrs) {
            const resolution = level.attrs.RESOLUTION;
            if (resolution) {
              const match = resolution.match(/(\d+)x(\d+)/);
              if (match) {
                height = parseInt(match[2], 10);
              }
            }
          }
          setCurrentQuality(getQualityLabel(height, level.bitrate));
        }
      });

      // Handle errors
      hls.on(Hls.Events.ERROR, (event, data) => {
        if (data.fatal) {
          switch (data.type) {
            case Hls.ErrorTypes.NETWORK_ERROR:
              hls.startLoad();
              break;
            case Hls.ErrorTypes.MEDIA_ERROR:
              hls.recoverMediaError();
              break;
            default:
              setIsLoading(false);
              break;
          }
        } else {
          setIsLoading(false);
        }
      });

      // Hide loading after timeout (safety net)
      const timeoutId = setTimeout(() => {
        setIsLoading(false);
      }, 5000);

      hlsRef.current = hls;

      return () => {
        clearTimeout(timeoutId);
        if (hlsRef.current) {
          hlsRef.current.destroy();
          hlsRef.current = null;
        }
      };
    } else if (channel.type === 'mp4' || video.canPlayType('application/vnd.apple.mpegurl')) {
      video.src = channel.url;
      // Auto-play for MP4
      setIsChannelReady(true);
      setIsLoading(false);
      if (!autoPlayDoneRef.current) {
        autoPlayDoneRef.current = true;
        const playPromise = video.play();
        if (playPromise !== undefined) {
          playPromise
            .then(() => {
              setIsPlaying(true);
            })
            .catch((err) => {
              console.log('Autoplay blocked:', err);
              setTimeout(() => {
                video.play().catch(() => {});
              }, 500);
            });
        }
      }
    } else if (channel.type === 'iframe') {
      video.style.display = 'none';
      const iframe = document.createElement('iframe');
      iframe.id = 'embed-iframe';
      iframe.src = channel.url;
      iframe.style.cssText = 'width:100%;height:100%;border:none;display:block;';
      iframe.allow = 'autoplay;fullscreen';
      iframe.allowFullscreen = true;
      const wrap = video.parentElement;
      wrap.appendChild(iframe);
      setIsChannelReady(true);
      setIsLoading(false);
    }

    return () => {
      if (hlsRef.current) {
        hlsRef.current.destroy();
        hlsRef.current = null;
      }
    };
  }, [channel]);

  // Get quality label based on height or bitrate
  const getQualityLabel = (height, bitrate) => {
    if (!height && !bitrate) return 'Auto';
    
    if (height >= 2160) return '4K';
    if (height >= 1440) return '2K';
    if (height >= 1080) return '1080p';
    if (height >= 720) return '720p';
    if (height >= 480) return '480p';
    if (height >= 360) return '360p';
    if (height >= 240) return '240p';
    if (height >= 144) return '144p';
    
    if (bitrate) {
      if (bitrate >= 8000000) return '4K';
      if (bitrate >= 4500000) return '1080p';
      if (bitrate >= 2500000) return '720p';
      if (bitrate >= 1500000) return '480p';
      if (bitrate >= 800000) return '360p';
      if (bitrate >= 400000) return '240p';
      if (bitrate >= 200000) return '144p';
    }
    
    return 'Auto';
  };

  // Change quality level
  const changeQuality = (levelIndex) => {
    const hls = hlsRef.current;
    if (!hls) return;

    if (levelIndex === 'auto') {
      hls.currentLevel = -1;
      setCurrentQuality('Auto');
    } else {
      hls.currentLevel = levelIndex;
      const level = hls.levels[levelIndex];
      if (level) {
        let height = level.height || 0;
        if (height === 0 && level.attrs) {
          const resolution = level.attrs.RESOLUTION;
          if (resolution) {
            const match = resolution.match(/(\d+)x(\d+)/);
            if (match) {
              height = parseInt(match[2], 10);
            }
          }
        }
        setCurrentQuality(getQualityLabel(height, level.bitrate));
      }
    }
    setShowQualityMenu(false);
  };

  // Video event listeners
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const handlePlay = () => {
      setIsPlaying(true);
      setIsLoading(false);
      setIsChannelReady(true);
    };
    const handlePause = () => setIsPlaying(false);
    const handleTimeUpdate = () => {
      if (video.duration) {
        setProgress((video.currentTime / video.duration) * 100);
      }
    };
    const handleLoadedMetadata = () => setDuration(video.duration);
    const handleCanPlay = () => {
      setIsLoading(false);
      setIsChannelReady(true);
    };
    const handleWaiting = () => {
      // Only show loading if not already playing
      if (!isPlaying) {
        setIsLoading(true);
      }
    };
    const handleCanPlayThrough = () => {
      setIsLoading(false);
    };

    video.addEventListener('play', handlePlay);
    video.addEventListener('pause', handlePause);
    video.addEventListener('timeupdate', handleTimeUpdate);
    video.addEventListener('loadedmetadata', handleLoadedMetadata);
    video.addEventListener('canplay', handleCanPlay);
    video.addEventListener('waiting', handleWaiting);
    video.addEventListener('canplaythrough', handleCanPlayThrough);

    return () => {
      video.removeEventListener('play', handlePlay);
      video.removeEventListener('pause', handlePause);
      video.removeEventListener('timeupdate', handleTimeUpdate);
      video.removeEventListener('loadedmetadata', handleLoadedMetadata);
      video.removeEventListener('canplay', handleCanPlay);
      video.removeEventListener('waiting', handleWaiting);
      video.removeEventListener('canplaythrough', handleCanPlayThrough);
    };
  }, []);

  // Auto-hide controls on mobile
  useEffect(() => {
    if (!isMobile) {
      setShowControls(true);
      return;
    }

    const handleInteraction = () => {
      setShowControls(true);
      if (controlsTimeout) clearTimeout(controlsTimeout);
      const timeout = setTimeout(() => setShowControls(false), 3000);
      setControlsTimeout(timeout);
    };

    const container = containerRef.current;
    if (container) {
      container.addEventListener('click', handleInteraction);
      container.addEventListener('touchstart', handleInteraction);
    }

    return () => {
      if (container) {
        container.removeEventListener('click', handleInteraction);
        container.removeEventListener('touchstart', handleInteraction);
      }
      if (controlsTimeout) clearTimeout(controlsTimeout);
    };
  }, [isMobile, controlsTimeout]);

  const togglePlay = () => {
    const video = videoRef.current;
    if (!video) return;
    if (video.paused) {
      video.play().catch((err) => {
        console.log('Play error:', err);
        setIsLoading(false);
      });
    } else {
      video.pause();
    }
  };

  // Toggle mute - when user unmutes, we store their preference
  const toggleMute = () => {
    const video = videoRef.current;
    if (!video) return;
    const newMuted = !video.muted;
    video.muted = newMuted;
    setIsMuted(newMuted);
    if (!newMuted) {
      video.volume = volume;
    }
  };

  const toggleFullscreen = useCallback(() => {
    const wrap = containerRef.current;
    if (!wrap) return;
    if (!document.fullscreenElement) {
      wrap.requestFullscreen?.();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen?.();
      setIsFullscreen(false);
    }
  }, []);

  const handleVolumeChange = (e) => {
    const video = videoRef.current;
    if (!video) return;
    const val = parseFloat(e.target.value);
    setVolume(val);
    video.volume = val;
    if (video.muted && val > 0) {
      video.muted = false;
      setIsMuted(false);
    }
  };

  const handleSeek = (e) => {
    const video = videoRef.current;
    if (!video || !duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = (e.clientX || e.touches?.[0]?.clientX || 0) - rect.left;
    const percentage = Math.max(0, Math.min(1, x / rect.width));
    video.currentTime = percentage * duration;
    setProgress(percentage * 100);
  };

  const formatTime = (seconds) => {
    if (!seconds || isNaN(seconds)) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (!channel) {
    return (
      <div style={{
        width: '100%',
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#050508',
        aspectRatio: '16/9',
        minHeight: '200px'
      }}>
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '8px',
          color: 'var(--muted)',
          fontSize: '14px',
          textAlign: 'center',
          padding: '20px'
        }}>
          <div style={{ fontSize: '40px' }}>📡</div>
          <div>একটি চ্যানেল সিলেক্ট করুন</div>
          <div style={{ fontSize: '12px' }}>Select a channel to start watching</div>
        </div>
      </div>
    );
  }

  return (
    <div ref={containerRef} style={{ position: 'relative', width: '100%', background: '#000' }}>
      <div style={{
        position: 'relative',
        width: '100%',
        aspectRatio: '16/9',
        background: '#000',
        touchAction: 'none'
      }}>
        <video
          ref={videoRef}
          controls={false}
          playsInline
          webkitPlaysInline="true"
          x5PlaysInline="true"
          muted={true} // Always muted for auto-play
          style={{
            width: '100%',
            height: '100%',
            display: 'block',
            background: '#000',
            touchAction: 'none'
          }}
        />

        {/* Loading overlay - only show when actually loading and not playing */}
        {isLoading && !isPlaying && (
          <div style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'rgba(0,0,0,0.7)',
            zIndex: 5
          }}>
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '12px',
              color: '#fff'
            }}>
              <div style={{
                width: '40px',
                height: '40px',
                border: '3px solid rgba(255,255,255,0.1)',
                borderTop: '3px solid var(--accent)',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite'
              }} />
              <div style={{ fontSize: '13px', opacity: 0.7 }}>
                {channel.name} লোড হচ্ছে...
              </div>
            </div>
          </div>
        )}

        {/* Custom Controls Overlay */}
        <div style={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          padding: isMobile ? '10px' : '16px',
          background: showControls ? 'linear-gradient(to bottom, rgba(0,0,0,.5) 0%, transparent 30%, transparent 70%, rgba(0,0,0,.7) 100%)' : 'transparent',
          pointerEvents: showControls ? 'auto' : 'none',
          transition: 'background 0.3s ease',
          opacity: showControls ? 1 : 0.6,
          zIndex: 6
        }}>
          {/* Top Controls */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            pointerEvents: 'auto',
            flexWrap: 'wrap',
            gap: '6px'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
              <span style={{
                background: 'var(--live)',
                color: '#fff',
                fontSize: isMobile ? '9px' : '11px',
                fontWeight: 700,
                padding: '2px 8px',
                borderRadius: '3px',
                letterSpacing: '0.5px'
              }}>● LIVE</span>
              {channel.cat === 'worldcup' && (
                <span style={{
                  background: 'var(--worldcup-glow)',
                  color: '#000',
                  fontSize: isMobile ? '8px' : '10px',
                  fontWeight: 800,
                  padding: '2px 6px',
                  borderRadius: '3px'
                }}>🏆 WC</span>
              )}
              {qualities.length > 0 && !isLoading && isPlaying && (
                <span style={{
                  background: 'rgba(0,0,0,0.7)',
                  color: '#fff',
                  fontSize: isMobile ? '8px' : '10px',
                  fontWeight: 600,
                  padding: '2px 8px',
                  borderRadius: '3px',
                  border: '1px solid rgba(255,255,255,0.1)'
                }}>
                  {currentQuality}
                </span>
              )}
            </div>
            <span style={{
              fontSize: isMobile ? '10px' : '12px',
              color: 'rgba(255,255,255,.7)',
              pointerEvents: 'auto'
            }}>👁 {formatNumber(channel.viewers)}</span>
          </div>

          {/* Center - Play/Pause Button (Mobile) */}
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            pointerEvents: 'auto',
            flex: 1
          }}>
            {isMobile && showControls && !isLoading && (
              <button
                onClick={togglePlay}
                style={{
                  background: 'rgba(0,0,0,0.6)',
                  border: '2px solid rgba(255,255,255,0.3)',
                  color: '#fff',
                  width: '60px',
                  height: '60px',
                  borderRadius: '50%',
                  fontSize: '28px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: '0.2s',
                  backdropFilter: 'blur(4px)'
                }}
              >
                {isPlaying ? '⏸' : '▶'}
              </button>
            )}
          </div>

          {/* Bottom Controls */}
          <div style={{
            pointerEvents: 'auto',
            display: showControls && !isLoading ? 'block' : 'none'
          }}>
            {/* Progress Bar */}
            <div
              onClick={handleSeek}
              style={{
                width: '100%',
                height: isMobile ? '6px' : '4px',
                background: 'rgba(255,255,255,0.2)',
                borderRadius: '2px',
                cursor: 'pointer',
                position: 'relative',
                marginBottom: '8px'
              }}
            >
              <div style={{
                width: `${progress}%`,
                height: '100%',
                background: 'var(--accent)',
                borderRadius: '2px',
                position: 'relative'
              }}>
                <div style={{
                  position: 'absolute',
                  right: '-6px',
                  top: '-5px',
                  width: isMobile ? '14px' : '10px',
                  height: isMobile ? '14px' : '10px',
                  background: 'var(--accent)',
                  borderRadius: '50%',
                  display: progress > 0 ? 'block' : 'none'
                }} />
              </div>
            </div>

            {/* Bottom Controls Row */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: isMobile ? '6px' : '12px',
              flexWrap: 'wrap'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? '6px' : '12px' }}>
                <button
                  onClick={togglePlay}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#fff',
                    fontSize: isMobile ? '18px' : '22px',
                    cursor: 'pointer',
                    padding: '4px'
                  }}
                >
                  {isPlaying ? '⏸' : '▶'}
                </button>

                <button
                  onClick={toggleMute}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#fff',
                    fontSize: isMobile ? '16px' : '18px',
                    cursor: 'pointer',
                    padding: '4px'
                  }}
                >
                  {isMuted ? '🔇' : '🔊'}
                </button>

                {!isMobile && (
                  <input
                    type="range"
                    min="0"
                    max="1"
                    step="0.01"
                    value={isMuted ? 0 : volume}
                    onChange={handleVolumeChange}
                    style={{
                      width: '60px',
                      height: '4px',
                      background: 'rgba(255,255,255,0.2)',
                      borderRadius: '2px',
                      outline: 'none',
                      cursor: 'pointer'
                    }}
                  />
                )}

                <span style={{
                  fontSize: isMobile ? '10px' : '12px',
                  color: 'rgba(255,255,255,.7)',
                  minWidth: '40px'
                }}>
                  {formatTime(videoRef.current?.currentTime || 0)}
                </span>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? '6px' : '12px' }}>
                {/* Quality Selector Button */}
                {qualities.length > 0 && !isLoading && isPlaying && (
                  <div style={{ position: 'relative' }}>
                    <button
                      onClick={() => setShowQualityMenu(!showQualityMenu)}
                      style={{
                        background: 'rgba(0,0,0,0.6)',
                        border: '1px solid rgba(255,255,255,0.2)',
                        color: '#fff',
                        fontSize: isMobile ? '10px' : '12px',
                        padding: '4px 10px',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px'
                      }}
                    >
                      ⚙️ {currentQuality}
                    </button>

                    {/* Quality Menu Dropdown */}
                    {showQualityMenu && (
                      <div style={{
                        position: 'absolute',
                        bottom: '100%',
                        right: 0,
                        marginBottom: '8px',
                        background: 'rgba(10,10,15,0.95)',
                        border: '1px solid var(--border)',
                        borderRadius: '8px',
                        padding: '6px 0',
                        minWidth: '140px',
                        backdropFilter: 'blur(10px)',
                        zIndex: 10,
                        boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
                        maxHeight: '300px',
                        overflowY: 'auto'
                      }}>
                        <button
                          onClick={() => changeQuality('auto')}
                          style={{
                            width: '100%',
                            padding: '8px 16px',
                            background: currentQuality === 'Auto' ? 'var(--accent)' : 'transparent',
                            border: 'none',
                            color: currentQuality === 'Auto' ? '#fff' : 'var(--text)',
                            fontSize: isMobile ? '12px' : '13px',
                            cursor: 'pointer',
                            textAlign: 'left',
                            transition: '0.2s',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px'
                          }}
                        >
                          <span>📶</span> Auto
                          {currentQuality === 'Auto' && <span style={{ marginLeft: 'auto' }}>✓</span>}
                        </button>

                        {qualities.map((level) => (
                          <button
                            key={level.index}
                            onClick={() => changeQuality(level.index)}
                            style={{
                              width: '100%',
                              padding: '8px 16px',
                              background: currentQuality === level.label ? 'var(--accent)' : 'transparent',
                              border: 'none',
                              color: currentQuality === level.label ? '#fff' : 'var(--text)',
                              fontSize: isMobile ? '12px' : '13px',
                              cursor: 'pointer',
                              textAlign: 'left',
                              transition: '0.2s',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '8px'
                            }}
                          >
                            <span>🎬</span> {level.label}
                            {currentQuality === level.label && <span style={{ marginLeft: 'auto' }}>✓</span>}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                <button
                  onClick={toggleFullscreen}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#fff',
                    fontSize: isMobile ? '16px' : '18px',
                    cursor: 'pointer',
                    padding: '4px'
                  }}
                >
                  {isFullscreen ? '⛶' : '⛶'}
                </button>
              </div>
            </div>

            {/* Channel Info */}
            <div style={{
              marginTop: '8px',
              display: isMobile ? 'block' : 'none'
            }}>
              <div style={{
                fontFamily: "'Teko', sans-serif",
                fontSize: '16px',
                fontWeight: 600,
                color: '#fff',
                textShadow: '0 2px 8px rgba(0,0,0,.8)'
              }}>{channel.name}</div>
              <div style={{
                fontSize: '11px',
                color: 'rgba(255,255,255,.7)',
                textShadow: '0 2px 8px rgba(0,0,0,.8)'
              }}>{channel.desc}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Player Info Bar */}
      <div style={{
        padding: isMobile ? '10px 12px' : '16px 20px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        borderTop: '1px solid var(--border)',
        flexWrap: 'wrap',
        gap: '8px',
        background: 'var(--surface)'
      }}>
        <div style={{ flex: 1, minWidth: '120px' }}>
          <div style={{
            fontFamily: "'Teko', sans-serif",
            fontSize: isMobile ? '18px' : '22px',
            fontWeight: 600,
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            flexWrap: 'wrap'
          }}>
            {channel.cat === 'worldcup' && '🏆 '}
            {channel.name}
            {qualities.length > 0 && !isLoading && isPlaying && (
              <span style={{
                fontSize: isMobile ? '10px' : '12px',
                color: 'var(--muted)',
                fontWeight: 400,
                background: 'var(--card)',
                padding: '2px 8px',
                borderRadius: '4px',
                border: '1px solid var(--border)'
              }}>
                {currentQuality}
              </span>
            )}
          </div>
          <div style={{
            fontSize: isMobile ? '10px' : '12px',
            color: 'var(--muted)',
            marginTop: '2px',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            flexWrap: 'wrap'
          }}>
            <span>{channel.cat.toUpperCase()}</span>
            <span>•</span>
            <span style={{ color: channel.status === 'live' ? 'var(--live)' : 'var(--muted)' }}>
              {channel.status === 'live' ? '🔴 LIVE' : '⚫ Offline'}
            </span>
            <span>•</span>
            <span>👁 {formatNumber(channel.viewers)}</span>
          </div>
        </div>

        <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
          {/* Mute/Unmute button - prominently displayed */}
          <button
            onClick={toggleMute}
            style={{
              background: isMuted ? 'var(--accent)' : 'var(--card)',
              border: `1px solid ${isMuted ? 'var(--accent)' : 'var(--border)'}`,
              color: isMuted ? '#fff' : 'var(--text)',
              padding: isMobile ? '5px 10px' : '7px 14px',
              borderRadius: '6px',
              fontSize: isMobile ? '11px' : '12px',
              cursor: 'pointer',
              transition: '0.2s',
              fontWeight: 600,
              whiteSpace: 'nowrap'
            }}
          >
            {isMuted ? '🔇 Unmute' : '🔊 Mute'}
          </button>

          {qualities.length > 0 && !isLoading && isPlaying && (
            <div style={{ position: 'relative' }}>
              <button
                onClick={() => setShowQualityMenu(!showQualityMenu)}
                style={{
                  background: 'var(--card)',
                  border: '1px solid var(--border)',
                  color: 'var(--text)',
                  padding: isMobile ? '5px 10px' : '7px 14px',
                  borderRadius: '6px',
                  fontSize: isMobile ? '11px' : '12px',
                  cursor: 'pointer',
                  transition: '0.2s',
                  fontWeight: 500,
                  whiteSpace: 'nowrap'
                }}
              >
                ⚙️ {currentQuality}
              </button>

              {showQualityMenu && (
                <div style={{
                  position: 'absolute',
                  bottom: '100%',
                  right: 0,
                  marginBottom: '8px',
                  background: 'rgba(10,10,15,0.98)',
                  border: '1px solid var(--border)',
                  borderRadius: '8px',
                  padding: '6px 0',
                  minWidth: '150px',
                  backdropFilter: 'blur(10px)',
                  zIndex: 10,
                  boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
                  maxHeight: '300px',
                  overflowY: 'auto'
                }}>
                  <button
                    onClick={() => changeQuality('auto')}
                    style={{
                      width: '100%',
                      padding: '10px 16px',
                      background: currentQuality === 'Auto' ? 'var(--accent)' : 'transparent',
                      border: 'none',
                      color: currentQuality === 'Auto' ? '#fff' : 'var(--text)',
                      fontSize: isMobile ? '13px' : '13px',
                      cursor: 'pointer',
                      textAlign: 'left',
                      transition: '0.2s',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px'
                    }}
                  >
                    <span>📶</span> Auto (Adaptive)
                    {currentQuality === 'Auto' && <span style={{ marginLeft: 'auto' }}>✓</span>}
                  </button>

                  {qualities.map((level) => (
                    <button
                      key={level.index}
                      onClick={() => changeQuality(level.index)}
                      style={{
                        width: '100%',
                        padding: '10px 16px',
                        background: currentQuality === level.label ? 'var(--accent)' : 'transparent',
                        border: 'none',
                        color: currentQuality === level.label ? '#fff' : 'var(--text)',
                        fontSize: isMobile ? '13px' : '13px',
                        cursor: 'pointer',
                        textAlign: 'left',
                        transition: '0.2s',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px'
                      }}
                    >
                      <span>🎬</span> {level.label}
                      {currentQuality === level.label && <span style={{ marginLeft: 'auto' }}>✓</span>}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          <button
            onClick={toggleFullscreen}
            style={{
              background: 'var(--card)',
              border: '1px solid var(--border)',
              color: 'var(--text)',
              padding: isMobile ? '5px 10px' : '7px 14px',
              borderRadius: '6px',
              fontSize: isMobile ? '11px' : '12px',
              cursor: 'pointer',
              transition: '0.2s',
              fontWeight: 500,
              whiteSpace: 'nowrap'
            }}
          >
            ⛶ Fullscreen
          </button>
        </div>
      </div>

      {/* Add spin animation */}
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

const formatNumber = (n) => {
  if (!n) return '0';
  if (n >= 1000000) return (n / 1000000).toFixed(1) + 'M';
  if (n >= 1000) return (n / 1000).toFixed(1) + 'K';
  return n.toString();
};

export default Player;